import { Component, markRaw } from 'vue';
import ChecklistService from '@/checklist/services/ChecklistService';
import MediaContext from '@/general/models/MediaContext';
import MediaContext2 from '@/general/models/MediaContext2';
import ReferenceService from '@/general/services/ReferenceService';
import Modal from '@/general/utils/Modal';
import DiagnosticType from '@/jobs/constants/DiagnosticType';
import JobContext from '@/jobs/models/JobContext';
import Diagnostic from '@/jobs/models/diagnosis/Diagnostic';
import { isPresent, present } from '@/shared/utils/typescript';
import DossierStackClient from '@/tasks/client/DossierStackClient';
import Memo from '@/tasks/models/Memo';
import TaskContext from '@/tasks/models/TaskContext';
import MemoService from '@/tasks/services/MemoService';
import TireStatus from '@/tires/constants/TireStatus';
import AxleSettingsModal from '@/tires/modals/AxleSettingsModal.vue';
import TireContext from '@/tires/models/TireContext';
import AxleService from '@/tires/services/AxleService';
import FluidClient from '../client/FluidClient';
import Dossier from '../models/Dossier';
import DossierContext from '../models/DossierContext';
import DossierDetailContext from '../models/DossierDetailContext';
import PaymentRequestContext from '../models/PaymentRequestContext';
import QualityControl from '../models/QualityControl';
import TestDrive from '../models/TestDrive';
import DossierService from './DossierService';
import DossierVehicleService from './DossierVehicleService';
import PaymentRequestService from './PaymentRequestService';
import TestDriveService from './TestDriveService';
import MediaTargetData from '@/general/models/MediaTargetData';
import DriveBookingService from '@/driveBookings/services/DriveBookingService';
import { DossierId } from '../models/AbstractDossier';
import DriveBookingContext from '@/driveBookings/models/DriveBookingContext';
import MediaFileService2 from '@/general/services/MediaFileService2';
import EditCarReservationModal from '@/driveBookings/modals/EditCarReservationModal.vue';
import ContactContext from '@/contact/models/ContactContext';

const AdviceDiagnosticModal = (): Component => import('@/jobs/modals/diagnosis/AdviceDiagnosticModal.vue');
const BooleanDiagnosticModal = (): Component => import('@/jobs/modals/diagnosis/BooleanDiagnosticModal.vue');
const CommentDiagnosticModal = (): Component => import('@/jobs/modals/diagnosis/CommentDiagnosticModal.vue');
const ComplaintDiagnosticModal = (): Component => import('@/jobs/modals/diagnosis/ComplaintDiagnosticModal.vue');
const DiagnosisDiagnosticModal = (): Component => import('@/jobs/modals/diagnosis/DiagnosisDiagnosticModal.vue');
const ObservationDiagnosticModal = (): Component => import('@/jobs/modals/diagnosis/ObservationDiagnosticModal.vue');
const PartsToStockDiagnosticModal = (): Component => import('@/jobs/modals/diagnosis/PartsToStockDiagnosticModal.vue');
const RepairDiagnosticModal = (): Component => import('@/jobs/modals/diagnosis/RepairDiagnosticModal.vue');
const ResultDiagnosticModal = (): Component => import('@/jobs/modals/diagnosis/ResultDiagnosticModal.vue');
const ReviewDiagnosticModal = (): Component => import('@/jobs/modals/diagnosis/ReviewDiagnosticModal.vue');
const AddMediaModal = (): Component => import('@/media/modals/AddMediaModal.vue');
const AddMediaModal2 = (): Component => import('@/media/modals/AddMediaModal2.vue');
const TireModal = (): Component => import('@/tires/modals/TireModal.vue');
const DossierStackAssignmentModal = (): Component => import('../modals/DossierStackAssignmentModal.vue');
const QualityControlModal = (): Component => import('../modals/QualityControlModal.vue');
const ChecklistTemplateModal = (): Component => import('../modals/details/ChecklistTemplateModal.vue');
const CleaningModal = (): Component => import('../modals/details/CleaningModal.vue');
const DossierAppointmentModal = (): Component => import('../modals/details/DossierAppointmentModal.vue');
const FluidModal = (): Component => import('../modals/details/FluidModal.vue');
const PaymentRequestModal = (): Component => import('../modals/details/PaymentRequestModal.vue');
const TestDriveModal = (): Component => import('../modals/details/TestDriveModal.vue');
const MemoModal = (): Component => import('../modals/tasks/MemoModal.vue');
const TireSwapModal = (): Component => import('@/tires/modals/TireSwapModal.vue');
const DriveBookingModal = (): Component => import('@/driveBookings/modals/DriveBookingModal.vue');
const CourtesyCarReservationModal = (): Component => import('../../driveBookings/modals/CourtesyCarReservationModal.vue');

class DossierModals {
  async appointment(dossier: Dossier): Promise<void> {
    await ReferenceService.loadMobilitySolutions(dossier.outletId);
    await Modal.open(DossierAppointmentModal, { dossier });
  }

  editCleaning(dossier: Dossier): Promise<void> {
    return Modal.openAndWait(CleaningModal, { dossier });
  }

  editQualityControl(context: DossierDetailContext, item?: QualityControl): Promise<void> {
    return Modal.open(QualityControlModal, { context, item });
  }

  async editTires(context: DossierContext): Promise<void> {
    if (present(context.tiresSummary).isMissingSettings) {
      await Modal.openAndWait(AxleSettingsModal, { context: TireContext.withDefaultSettings(context) });
    }
    const tireContext = await AxleService.loadByDossier(context);
    await ReferenceService.loadTireMakes();
    Modal.openAndWait(TireModal, { context: tireContext }).finally(() => DossierService.doLoadTireSummary(context));
  }

  async swapTires(context: TireContext, status: TireStatus): Promise<void> {
    await Modal.openAndWait(TireSwapModal, { context, currentStatus: status });
    await AxleService.swap(context);
  }

  async editTestDrive(context: DossierDetailContext, testDrive?: TestDrive): Promise<void> {
    const brandId = DossierVehicleService.getCurrent().vehicle?.brand?.id;
    const reasons = isPresent(brandId) ? await TestDriveService.loadReasons(brandId) : [];
    await Modal.open(TestDriveModal, { context, testDrive, reasons });
  }

  async addPaymentRequest(context: PaymentRequestContext): Promise<void> {
    await PaymentRequestService.loadInvoiceFiles(context);
    await Modal.openAndWait(PaymentRequestModal, { context });
  }

  async fluidOverview(context: DossierContext): Promise<void> {
    context.fluids = await FluidClient.loadAll(context.dossier.id);
    await Modal.open(FluidModal, { context });
  }

  async addChecklist(context: DossierDetailContext): Promise<void> {
    const checklistTemplates = await ChecklistService.loadAllTemplates(context.dossier.id);
    await Modal.open(ChecklistTemplateModal, { checklistTemplates, context });
  }

  addMedia(context: MediaContext): Promise<void> {
    return Modal.open(AddMediaModal, { context });
  }

  addMedia2(context: MediaContext2, targetData: MediaTargetData): Promise<void> {
    return Modal.open(AddMediaModal2, { context, targetData });
  }

  async memo(context: TaskContext, memo?: Memo): Promise<void> {
    const tags = await MemoService.loadTags();
    await Modal.open(MemoModal, { memo, context, tags });
  }

  async assignToStack(dossier: Dossier): Promise<void> {
    const stacks = await DossierStackClient.loadAll(dossier.id);
    const availableStacks = stacks.filter(stack => !dossier.stacks.some(s => s.id === stack.id)).map(markRaw);
    await Modal.open(DossierStackAssignmentModal, { dossier, stacks: availableStacks });
  }

  diagnostic(diagnostic: Diagnostic, context: JobContext, mediaContext?: MediaContext2, job = context.getSelectedJob()): void {
    Modal.open(this.getDiagnosticComponent(diagnostic.type), { diagnostic, context, mediaContext, job });
  }

  async viewDriveBooking(context: DriveBookingContext, driveBookingId: number, dossierId: DossierId): Promise<void> {
    await DriveBookingService.loadDetails(driveBookingId, context);

    if (!context.selectedDriveBooking) {
      return;
    }

    const mediaChannel = `drivebookings-${driveBookingId}/media-processed`;
    const driveBookingMediaContext = await MediaFileService2.loadForFiles(context.selectedDriveBooking.medias, dossierId, mediaChannel);

    return Modal.open(DriveBookingModal, {
      context,
      mediaContext: driveBookingMediaContext,
    });
  }

  addCourtesyCarReservation(dossier: Dossier, driveContext: DriveBookingContext): void {
    Modal.open(CourtesyCarReservationModal, { dossier, driveContext });
  }

  editCourtesyCarReservation(driveContext: DriveBookingContext, contactContext: ContactContext) {
    return Modal.openAndWait(
      EditCarReservationModal,
      {
        driveContext,
        contactContext,
      },
    );
  }

  private getDiagnosticComponent(type: DiagnosticType): Component {
    switch (type) {
      case DiagnosticType.COMMENT:
      case DiagnosticType.TEXT:
        return CommentDiagnosticModal;
      case DiagnosticType.COMPLAINT:
        return ComplaintDiagnosticModal;
      case DiagnosticType.OBSERVATION:
        return ObservationDiagnosticModal;
      case DiagnosticType.DIAGNOSIS:
        return DiagnosisDiagnosticModal;
      case DiagnosticType.ADVICE:
        return AdviceDiagnosticModal;
      case DiagnosticType.REPAIR:
        return RepairDiagnosticModal;
      case DiagnosticType.RESULT:
        return ResultDiagnosticModal;
      case DiagnosticType.PARTS_TO_STOCK:
        return PartsToStockDiagnosticModal;
      case DiagnosticType.REVIEW:
        return ReviewDiagnosticModal;
      case DiagnosticType.BOOLEAN:
        return BooleanDiagnosticModal;
    }
  }
}

export default new DossierModals();
